import React, { type ReactNode } from 'react';
import Markdown from 'react-markdown';

import {
    Align,
    Box,
    BoxContent,
    DirectionEnum,
    FlexBox,
    FlexItem,
    GapSize,
    Justify,
    Price,
    PriceLevel,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyBold,
} from '@bytel/trilogy-react';

import { DiscountType } from '@app-types/discount';
import { MobilePlanType } from '@app-types/plan';
import { PriceType } from '@app-types/price';

import { BigIcon, BigIconVariant } from '@components/big';
import { InlinePrice } from '@components/inline-price';

interface BigInfoBoxProps {
    offer: MobilePlanType;
    offerPrices: PriceType;
    label: string;
    popover?: ReactNode;
    loggedInDiscount?: DiscountType;
}

export function BigInfoBox({ offer, offerPrices, label, popover, loggedInDiscount }: Readonly<BigInfoBoxProps>) {
    return (
        <Box
            highlighted={TrilogyColor.ACCENT}
            shadowless
            backgroundColor={TrilogyColor.ACCENT_FADE}
            padding='md'
            radius='md'
        >
            <BoxContent>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                    <FlexBox justify={Justify.SPACE_BETWEEN} direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                        <FlexBox align={Align.CENTER} gap={GapSize.THREE} direction={DirectionEnum.ROW}>
                            <BigIcon height={12} variant={BigIconVariant.ACCENT} />
                            <FlexItem>
                                <Text marginless level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    {label} {popover}
                                </Text>
                                {loggedInDiscount?.value && <Text level={TextLevels.THREE}>Déjà déduite</Text>}
                            </FlexItem>
                        </FlexBox>
                        <FlexBox align={Align.END}>
                            <Text level={TextLevels.ONE} marginless>
                                <Price
                                    amount={loggedInDiscount?.value ? -loggedInDiscount.value : offerPrices.final}
                                    level={PriceLevel.SIX}
                                    period='mois'
                                />
                            </Text>
                            {Boolean(offerPrices.duration) && !loggedInDiscount && (
                                <Text level={TextLevels.THREE} marginless>
                                    puis{' '}
                                    <InlinePrice
                                        level={TextLevels.THREE}
                                        amount={offerPrices.forever}
                                        period='mois'
                                        typo={TypographyBold.TEXT_WEIGHT_NORMAL}
                                    />
                                    &nbsp;après {offerPrices.duration} mois
                                </Text>
                            )}
                        </FlexBox>
                    </FlexBox>

                    {offer.cmsContent?.bigBenefit && (
                        <Text level={TextLevels.THREE}>
                            <Markdown>{offer.cmsContent.bigBenefit}</Markdown>
                        </Text>
                    )}
                </FlexBox>
            </BoxContent>
        </Box>
    );
}
